<template>
  <Dialog v-model="dialogVisible" :title="dialogTitle" width="65%">
    <Form
      ref="formRef"
      v-loading="formLoading"
      :isCol="true"
      :rules="formRules"
      :schema="formSchema"
    >
      <!-- 先选择 -->
      <template #spuId>
        <el-button @click="spuSelectRef.open()">选择商品</el-button>
        <SpuAndSkuList
          ref="spuAndSkuListRef"
          :rule-config="ruleConfig"
          :spu-list="spuList"
          :spu-property-list-p="spuPropertyList"
        >
          <el-table-column align="center" label="秒杀库存" min-width="168">
            <template #default="{ row: sku }">
              <el-input-number
                v-model="sku.productConfig.stock"
                :max="sku.stock"
                :min="1"
                class="w-100%"
              />
            </template>
          </el-table-column>
          <el-table-column align="center" label="秒杀价格(元)" min-width="168">
            <template #default="{ row: sku }">
              <el-input-number
                v-model="sku.productConfig.seckillPrice"
                :min="0"
                :precision="2"
                :step="0.1"
                class="w-100%"
              />
            </template>
          </el-table-column>
        </SpuAndSkuList>
      </template>
    </Form>
    <template #footer>
      <el-button :disabled="formLoading" type="primary" @click="submitForm">确 定</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
  <SpuSelect ref="spuSelectRef" :isSelectSku="true" :merchant="props.merchant" @confirm="selectSpu" />
</template>
<script lang="ts" setup>
import { SpuAndSkuList, SpuProperty, SpuSelect } from '../../components'
import { allSchemas, createEndTimeAfterStartRule, rules } from './seckillActivity.data'
import { cloneDeep } from 'lodash-es'

import * as AdminSeckillActivityApi from '@/api/mall/promotion/seckill/seckillActivity'
import { SeckillConfigApi as AdminSeckillConfigApi } from '@/api/mall/promotion/seckill/seckillConfig'
import * as MerchantPromotionApi from '@/api/merchant/promotion'
import type {
  SeckillActivityVO,
  SeckillProductVO,
  SkuExtension,
  SpuExtension
} from '@/api/mall/promotion/seckill/seckillActivity'
import * as AdminProductSpuApi from '@/api/mall/product/spu'
import * as MerchantProductApi from '@/api/merchant/product'
import { getPropertyList, RuleConfig } from '@/views/mall/product/spu/components'
import { convertToInteger, formatToFraction } from '@/utils'

defineOptions({ name: 'PromotionSeckillActivityForm' })

const props = defineProps({ merchant: { type: Boolean, default: false } })
const SeckillActivityApi = computed(() => props.merchant ? MerchantPromotionApi : AdminSeckillActivityApi)
// 秒杀时段下拉选项（按平台/商家身份懒加载）
const seckillConfigOptions = ref<any[]>([])
const loadSeckillConfigOptions = async () => {
  seckillConfigOptions.value = props.merchant
    ? await MerchantPromotionApi.getSimpleSeckillConfigList()
    : await AdminSeckillConfigApi.getSimpleSeckillConfigList()
}

const formSchema = computed(() => {
  return allSchemas.formSchema.map((s: any) => {
    if (s.field === 'configIds') {
      return {
        ...s,
        componentProps: {
          ...s.componentProps,
          options: seckillConfigOptions.value
        }
      }
    }
    return s
  })
})
const ProductSpuApi = computed(() => props.merchant ? MerchantProductApi : AdminProductSpuApi)

const { t } = useI18n() // 国际化
const message = useMessage() // 消息弹窗

const dialogVisible = ref(false) // 弹窗的是否展示
const dialogTitle = ref('') // 弹窗的标题
const formLoading = ref(false) // 表单的加载中：1）修改时的数据加载；2）提交的按钮禁用
const formType = ref('') // 表单的类型：create - 新增；update - 修改
const formRef = ref() // 表单 Ref
/** 结束时间须晚于开始时间 */
const formRules = reactive({
  ...rules,
  endTime: [createEndTimeAfterStartRule(() => formRef.value?.formModel?.startTime)]
})

// ================= 商品选择相关 =================

const spuSelectRef = ref() // 商品和属性选择 Ref
const spuAndSkuListRef = ref() // sku 秒杀配置组件Ref
const ruleConfig: RuleConfig[] = [
  {
    name: 'productConfig.stock',
    // 秒杀库存：至少 1，且不能超过该 SKU 原有库存（sku.stock）
    rule: (arg, sku) => arg >= 1 && (sku?.stock == null || arg <= sku.stock),
    message: '秒杀库存不能小于 1，且不能超过商品原有库存'
  },
  {
    name: 'productConfig.seckillPrice',
    rule: (arg) => arg >= 0.01,
    message: '商品秒杀价格必须大于等于 0.01 ！！！'
  }
]
const spuList = ref<SpuExtension[]>([]) // 选择的 spu
const spuPropertyList = ref<SpuProperty<SpuExtension>[]>([])
const selectSpu = (spuId: number, skuIds: number[]) => {
  formRef.value.setValues({ spuId })
  getSpuDetails(spuId, skuIds)
}
/**
 * 获取 SPU 详情
 */
const getSpuDetails = async (
  spuId: number,
  skuIds: number[] | undefined,
  products?: SeckillProductVO[]
) => {
  const spuProperties: SpuProperty<SpuExtension>[] = []
  const res = (await ProductSpuApi.value.getSpuDetailList([spuId])) as AdminSeckillActivityApi.SpuExtension[]
  if (res.length == 0) {
    message.warning('未找到关联商品，可能已删除或无查询权限')
    return
  }
  spuList.value = []
  // 因为只能选择一个
  const spu = res[0]
  const skuIdSet =
    typeof skuIds === 'undefined' ? null : new Set(skuIds.map((id) => Number(id)))
  const selectSkus =
    skuIdSet == null ? spu?.skus : spu?.skus?.filter((sku) => skuIdSet.has(Number(sku.id)))
  selectSkus?.forEach((sku) => {
    let config: SeckillProductVO = {
      spuId: spu.id!,
      skuId: sku.id!,
      stock: 0,
      seckillPrice: 0
    }
    if (typeof products !== 'undefined') {
      const product = products.find((item) => Number(item.skuId) === Number(sku.id))
      if (product) {
        product.seckillPrice = Number(formatToFraction(product.seckillPrice))
      }
      config = product || config
    }
    sku.productConfig = config
  })
  if (!selectSkus?.length) {
    message.warning('活动关联的 SKU 已不存在，请重新选择商品')
  }
  spu.skus = selectSkus as SkuExtension[]
  spuProperties.push({
    spuId: spu.id!,
    spuDetail: spu,
    propertyList: getPropertyList(spu)
  })
  spuList.value.push(spu)
  spuPropertyList.value = spuProperties
}

// ================= end =================

/** 打开弹窗 */
const open = async (type: string, id?: number) => {
  dialogVisible.value = true
  dialogTitle.value = t('action.' + type)
  formType.value = type
  // Dialog 为 destroy-on-close：先等 Form 挂载再 reset/回填，否则 formRef 为空会中断回显
  await nextTick()
  await resetForm()
  // 加载秒杀时段选项
  await loadSeckillConfigOptions()
  // 修改时，设置数据
  if (id) {
    formLoading.value = true
    try {
      const data = (await SeckillActivityApi.value.getSeckillActivity(
        id
      )) as SeckillActivityVO
      // 先回填活动字段；商品明细失败时至少名称/时间等仍可见
      formRef.value?.setValues({
        ...data,
        configIds: normalizeConfigIds(data.configIds)
      })
      try {
        await getSpuDetails(
          data.spuId!,
          data.products?.map((sku) => sku.skuId),
          data.products
        )
      } catch (e) {
        console.error(e)
        message.error('秒杀商品明细加载失败，请确认商品仍存在且具备商品查询权限')
      }
    } finally {
      formLoading.value = false
    }
  }
}
defineExpose({ open }) // 提供 open 方法，用于打开弹窗

/** 秒杀时段 id 统一为 number[]，避免 Select 多选无法回显 */
const normalizeConfigIds = (configIds: SeckillActivityVO['configIds']) => {
  if (Array.isArray(configIds)) {
    return configIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
  }
  if (configIds == null || configIds === '') {
    return []
  }
  return String(configIds)
    .split(',')
    .map((id) => Number(id.trim()))
    .filter((id) => !Number.isNaN(id))
}
/** 提交表单 */
const emit = defineEmits(['success']) // 定义 success 事件，用于操作成功后的回调
const submitForm = async () => {
  // 校验表单
  if (!formRef.value) return
  try {
    await formRef.value.getElFormRef().validate()
  } catch {
    return
  }
  const data = cloneDeep(formRef.value.formModel) as SeckillActivityVO
  if (
    data.startTime != null &&
    data.endTime != null &&
    Number(data.endTime) <= Number(data.startTime)
  ) {
    message.error('活动结束时间必须晚于开始时间')
    return
  }
  // 提交请求
  formLoading.value = true
  try {
    // 获取秒杀商品配置（内部会按 ruleConfig 校验秒杀库存 ≤ 原库存）
    const products = cloneDeep(spuAndSkuListRef.value.getSkuConfigs('productConfig'))
    // 双保险：对照当前已选 SKU 原库存再拦一次
    for (const product of products as SeckillProductVO[]) {
      const sku = spuList.value
        .flatMap((spu) => spu.skus || [])
        .find((item) => Number(item.id) === Number(product.skuId))
      const skuStock = sku?.stock
      if (skuStock != null && Number(product.stock) > Number(skuStock)) {
        message.error(`秒杀库存不能超过商品原有库存（原库存 ${skuStock}）`)
        return
      }
    }
    products.forEach((item: SeckillProductVO) => {
      item.seckillPrice = convertToInteger(item.seckillPrice)
    })
    data.products = products
    // 真正提交
    if (formType.value === 'create') {
      await SeckillActivityApi.value.createSeckillActivity(data)
      message.success(t('common.createSuccess'))
    } else {
      await SeckillActivityApi.value.updateSeckillActivity(data)
      message.success(t('common.updateSuccess'))
    }
    dialogVisible.value = false
    // 发送操作成功的事件
    emit('success')
  } catch {
    // validateSku 失败会抛错，已在组件内提示
  } finally {
    formLoading.value = false
  }
}

/** 重置表单 */
const resetForm = async () => {
  spuList.value = []
  spuPropertyList.value = []
  await nextTick()
  formRef.value?.getElFormRef()?.resetFields()
}
</script>
